import { useEffect, useMemo, useRef } from "react";

import { Button, Group, Stack } from "@mantine/core";
import { DateInput, TimeInput } from "@mantine/dates";
import { UseFormReturnType } from "@mantine/form";

import { IconCalendar, IconClock } from "@tabler/icons-react";
import { format, parseISO } from "date-fns";

import { TextWrapper } from "@/components/TextWrapper";
import { useStableCalendarDateInputState } from "@/intake-forms/hooks/useStableCalendarDateInputState";
import { setFormFieldValue } from "@/intake-forms/utils/intake-forms";
import { formatInitiatedTimestamp, isValidTimeString } from "@/utils/timeUtils";

interface QuestionnaireTimestampDisplayProps<T> {
  form: UseFormReturnType<T>;
  formFieldName: string;
  autoPopulate?: boolean;
  /** When true, shows date/time inputs to edit the timestamp. Defaults to false (read-only display). */
  editable?: boolean;
}

/**
 * Timestamp display for questionnaire dateTime fields with `timestamp-display` control.
 * Auto-populates with the current datetime when empty. Read-only by default; set `editable`
 * (or questionnaire `questionnaire-field-timestamp-editable` extension) to allow edits.
 *
 * Format: "Initiated 10:49:20 AM · September 12, 2025"
 */
export default function QuestionnaireTimestampDisplay<T>({
  form,
  formFieldName,
  autoPopulate = true,
  editable = false,
}: QuestionnaireTimestampDisplayProps<T>) {
  const timeInputRef = useRef<HTMLInputElement>(null);
  const formInputProps = form.getInputProps(formFieldName);
  const currentValue = formInputProps.value as string | undefined;

  useEffect(() => {
    if (autoPopulate && !currentValue) {
      const now = new Date().toISOString();
      setFormFieldValue(form, formFieldName, now);
    }
  }, [autoPopulate, currentValue, form, formFieldName]);

  const displayText = formatInitiatedTimestamp(currentValue);

  const { stableDateValue, calendarDate, handleCalendarDateChange } =
    useStableCalendarDateInputState(currentValue);

  const timeValue = useMemo(
    () => (currentValue ? format(new Date(currentValue), "HH:mm:ss") : ""),
    [currentValue],
  );

  const setTimestampValue = (isoString: string | null) => {
    setFormFieldValue(form, formFieldName, isoString);
  };

  const handleNowClick = () => {
    setTimestampValue(new Date().toISOString());
  };

  const handleDateChange = (value: string | null) => {
    if (!value) {
      setTimestampValue(null);
      return;
    }

    const timeSource = currentValue ? new Date(currentValue) : new Date();
    const merged = parseISO(value);
    merged.setHours(
      timeSource.getHours(),
      timeSource.getMinutes(),
      timeSource.getSeconds(),
      timeSource.getMilliseconds(),
    );
    setTimestampValue(merged.toISOString());
  };

  const handleTimeInputClick = () => {
    timeInputRef.current?.showPicker();
  };

  const handleTimeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.currentTarget;

    if (!value) {
      setTimestampValue(null);
      return;
    }

    if (!isValidTimeString(value)) {
      return;
    }

    const dateSource = currentValue ? new Date(currentValue) : new Date();
    const [hours, minutes, seconds] = value.split(":").map(Number);
    dateSource.setHours(hours, minutes, seconds);
    setTimestampValue(dateSource.toISOString());
  };

  if (!editable) {
    if (!displayText) {
      return null;
    }

    return (
      <TextWrapper size="sm" c="dimmed" data-testid="timestamp-display-read-only">
        {displayText}
      </TextWrapper>
    );
  }

  return (
    <Stack gap="xs" data-testid="timestamp-display-editable">
      {displayText && (
        <TextWrapper size="sm" c="dimmed" data-testid="timestamp-display-initiated-summary">
          {displayText}
        </TextWrapper>
      )}
      <Group align="flex-end">
        <DateInput
          w={175}
          label="Date"
          date={calendarDate}
          allowDeselect={false}
          value={stableDateValue}
          valueFormat="MM/DD/YYYY"
          placeholder="MM/DD/YYYY"
          clearable={!autoPopulate}
          onChange={handleDateChange}
          data-testid="timestamp-display-date"
          onDateChange={handleCalendarDateChange}
          leftSection={<IconCalendar size={20} />}
        />
        <TimeInput
          withSeconds
          label="Time"
          value={timeValue}
          ref={timeInputRef}
          onChange={handleTimeChange}
          onClick={handleTimeInputClick}
          data-testid="timestamp-display-time"
          leftSection={<IconClock size={20} stroke={1.5} />}
        />
        <Button
          size="sm"
          variant="outline"
          color="burgundy.9"
          onClick={handleNowClick}
          data-testid="timestamp-display-now"
        >
          Now
        </Button>
      </Group>
    </Stack>
  );
}
